import type { ReactNode } from "react";
import { Badge, IconButton, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

export type AssignedState = "any" | "assigned" | "unassigned";

export type AssignedStateToggleProps = {
  label: string;
  icon: ReactNode;
  value: AssignedState;
  onChange: (next: AssignedState) => void;
};

const cycle: Record<AssignedState, { next: AssignedState; hint: string }> = {
  any: { next: "assigned", hint: "tap to show only assigned" },
  assigned: { next: "unassigned", hint: "tap to show only unassigned" },
  unassigned: { next: "any", hint: "tap to show all" },
};

const stateLabel: Record<AssignedState, string> = {
  any: "any",
  assigned: "only assigned",
  unassigned: "only unassigned",
};

export const AssignedStateToggle = ({
  label,
  icon,
  value,
  onChange,
}: AssignedStateToggleProps) => {
  const { next, hint } = cycle[value];
  const title = `${label} filter: ${stateLabel[value]} (${hint})`;

  const iconColour = value === "assigned" ? "primary" : "default";

  const overlay =
    value === "assigned" ? (
      <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
    ) : value === "unassigned" ? (
      <CancelIcon sx={{ fontSize: 14, color: "error.main" }} />
    ) : null;

  return (
    <Tooltip title={title}>
      <IconButton
        color={iconColour}
        onClick={() => onChange(next)}
        aria-label={title}
        sx={{ p: 1, opacity: value === "any" ? 0.55 : 1 }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={overlay}
        >
          {icon}
        </Badge>
      </IconButton>
    </Tooltip>
  );
};
